import { Patient } from "@/lib/types/patient"
import * as z from "zod"

export type VisitStatus = "scheduled" | "in-progress" | "completed" | "cancelled"
export type VisitType = "regular" | "outreach"

export const VISIT_REASONS = [
  "Regular Check-up",
  "Vaccination",
  "Follow-up",
  "Emergency",
  "Consultation",
  "Other"
] as const

export type VisitReason = typeof VISIT_REASONS[number]

export const visitSchema = z.object({
  patientId: z.string(),
  clinicId: z.string(),
  doctorId: z.string(),
  startTime: z.string(),
  duration: z.string(),
  reason: z.string(),
  otherReason: z.string().optional(),
  initialNotes: z.string().optional(),
  type: z.enum(["regular", "outreach"]),
  isUrgent: z.boolean().optional(),
})

export interface Vitals {
  temperature?: number
  bloodPressure?: string
  heartRate?: number
  respiratoryRate?: number
  oxygenSaturation?: number
  height?: number
  weight?: number
  bmi?: number
  bloodSugar?: number
}

export interface Visit {
  id: string
  patientId: string
  clinicId: string
  doctorId: string
  patient?: Patient
  title?: string // Display title (e.g. "Regular Check-up - John Doe")
  type: VisitType
  outreachProgramId?: string // ID of associated outreach program
  physicianReminders?: {
    id: string
    date: string // ISO date string
    notes?: string
    isUrgent?: boolean
  }[]
  startTime: string // ISO date string
  endTime: string // ISO date string - computed from startTime + duration
  duration: number // in minutes
  status: VisitStatus
  reason: string
  initialNotes?: string
  notes?: string
  vitals?: Vitals
  symptoms?: string[]
  diagnosis?: string
  prescriptions?: {
    medication: string
    dosage: string
    frequency: string
    duration: string
    notes?: string
  }[]
  prescriptionChanges?: {
    prescriptionId: string
    medication: string
    changeType: 'activated' | 'deactivated'
    timestamp: string
  }[]
  inventoryUsage?: {
    itemId: string
    quantity: number
    notes?: string
  }[]
  originalVisitId?: string // Reference to the visit this is a followup for
  isUrgent?: boolean // Whether this is an urgent followup
  patientName?: string // Denormalized for convenience
  createdAt: string // ISO date string
  updatedAt: string // ISO date string
}

export interface CreateVisitInput {
  patientId: string
  clinicId: string
  doctorId: string
  type: VisitType
  outreachProgramId?: string // ID of associated outreach program
  startTime: string // ISO date string
  duration: number
  reason: string
  initialNotes?: string
  originalVisitId?: string // Reference to the visit this is a followup for
  isUrgent?: boolean
}

export interface UpdateVisitInput {
  id: string
  status?: VisitStatus
  notes?: string
  vitals?: Vitals
  symptoms?: string[]
  diagnosis?: string
  medicalHistory?: string
  physicianReminders?: {
    id: string
    date: string // ISO date string
    notes?: string
    isUrgent?: boolean
  }[]
  prescriptions?: {
    medication: string
    dosage: string
    frequency: string
    duration: string
    notes?: string
  }[]
  inventoryUsage?: {
    itemId: string
    quantity: number
    notes?: string
  }[]
  requiresFollowUp?: boolean
  followUpCompleted?: boolean
  followUpDate?: string
  followUpReason?: string
  followUpUrgent?: boolean
}
